import { useEffect, useRef, useState } from 'react';
import { Icon } from '../../lib/icons';
import { PAGE_DOWNLOAD_MAX_BYTES, canTrackDownloads, downloadWithProgress, fractionOf, nativeDownload, type DownloadProgress } from '../../lib/download';

/**
 * A file's round download button, whose border fills like a clock while the file downloads (Joey,
 * Oct 29 2026). Selecting it again stops the download. When the size isn't known the border spins
 * instead. Very big files, and browsers that can't show progress, download the browser's own way.
 */
export function DownloadButton({ url, name, bytes, label }: { url: string; name: string; bytes?: number | null; label?: string }) {
  const [progress, setProgress] = useState<DownloadProgress | null>(null);
  const [done, setDone] = useState(false);
  const running = useRef<AbortController | null>(null);
  const doneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      running.current?.abort();
      if (doneTimer.current) clearTimeout(doneTimer.current);
    },
    []
  );

  async function start() {
    if (running.current) {
      // Selected again while it downloads: stop.
      running.current.abort();
      return;
    }
    if (!canTrackDownloads() || (bytes ?? 0) > PAGE_DOWNLOAD_MAX_BYTES) {
      nativeDownload(url, name);
      return;
    }
    const ctl = new AbortController();
    running.current = ctl;
    setDone(false);
    setProgress({ received: 0, total: bytes ?? null });
    let last = 0;
    try {
      await downloadWithProgress(url, name, {
        bytes: bytes ?? null,
        signal: ctl.signal,
        onProgress: (p) => {
          // A redraw at most every 1% (or for the last bytes), not for every piece that arrives.
          const f = fractionOf(p);
          if (f === null || f === 1 || f - last >= 0.01) {
            last = f ?? 0;
            setProgress(p);
          }
        }
      });
      setDone(true);
      doneTimer.current = setTimeout(() => setDone(false), 1500);
    } catch (err) {
      // Stopped by the person: nothing more. Anything else: the browser's own way, which says why.
      if ((err as Error)?.name !== 'AbortError') nativeDownload(url, name);
    } finally {
      if (running.current === ctl) running.current = null;
      setProgress(null);
    }
  }

  const busy = progress !== null;
  const fraction = fractionOf(progress);
  const percent = fraction === null ? null : Math.round(fraction * 100);
  const what = label ?? name;
  const aria = busy ? `Downloading ${what}${percent === null ? '' : `: ${percent}%`}. Select to stop.` : done ? `Downloaded ${what}` : `Download ${what}`;
  // The ring sits on the button's own border: a 36 px circle, its 2 px line centred on the edge.
  const r = 17;
  const around = 2 * Math.PI * r;
  return (
    <button
      type="button"
      className={`icon-btn msg-file-get${busy ? ' downloading' : ''}${done ? ' downloaded' : ''}`}
      aria-label={aria}
      title={busy ? (percent === null ? 'Downloading… (select to stop)' : `Downloading ${percent}% (select to stop)`) : done ? 'Downloaded' : 'Download'}
      aria-busy={busy || undefined}
      onClick={() => void start()}
    >
      {(busy || done) && (
        <svg className={`dl-ring${busy && fraction === null ? ' unknown' : ''}`} viewBox="0 0 36 36" aria-hidden="true">
          <circle className="dl-ring-track" cx="18" cy="18" r={r} />
          <circle
            className="dl-ring-fill"
            cx="18"
            cy="18"
            r={r}
            strokeDasharray={around}
            strokeDashoffset={done ? 0 : fraction === null ? around * 0.72 : around * (1 - fraction)}
          />
        </svg>
      )}
      <Icon name={done ? 'check' : 'download'} size={15} />
      {/* Said once when it starts and once when it's done, not at every percent. */}
      <span className="sr-only" aria-live="polite">
        {busy ? 'Downloading' : done ? 'Downloaded' : ''}
      </span>
    </button>
  );
}
